/**
 * Front-end behavior for testimonial slider block.
 */

document.addEventListener( 'DOMContentLoaded', () => {
	const supportsPointerEvents = 'PointerEvent' in window;
	const supports3dTransform =
		typeof window.CSS !== 'undefined' &&
		typeof window.CSS.supports === 'function' &&
		window.CSS.supports( 'transform', 'translate3d(1px, 0, 0)' );
	const prefersReducedMotion =
		typeof window.matchMedia === 'function' &&
		window.matchMedia( '(prefers-reduced-motion: reduce)' ).matches;

	const blocks = document.querySelectorAll(
		'.wp-block-telex-block-telex-testimonial-grid-slider'
	);

	blocks.forEach( ( block ) => {
		const grid = block.querySelector( '.testimonials-grid' );

		if ( ! grid ) {
			return;
		}

		const prevBtn = block.querySelector( '.prev-btn' );
		const nextBtn = block.querySelector( '.next-btn' );
		const dotsContainer = block.querySelector( '.slider-dots' );

		let viewport = block.querySelector( '.testimonials-viewport' );
		if ( ! viewport ) {
			viewport = document.createElement( 'div' );
			viewport.className = 'testimonials-viewport';
			grid.parentNode.insertBefore( viewport, grid );
			viewport.appendChild( grid );
		}

		let currentIndex = 0;
		let trackIndex = 1;
		let totalSlides = 0;
		let slideStep = 0;
		let isAnimating = false;
		let isDragging = false;
		let dragActive = false;
		let startX = 0;
		let currentX = 0;
		let startY = 0;
		let activeDragSource = '';
		let activePointerId = null;
		let resizeTimer = null;

		const isMobile = () => window.innerWidth <= 767;
		const toTranslate = ( value ) =>
			supports3dTransform
				? `translate3d(${ value }px, 0, 0)`
				: `translateX(${ value }px)`;
		const getOriginalSlides = () =>
			Array.from( grid.children ).filter(
				( card ) =>
					card.classList.contains( 'testimonial-card' ) &&
					card.dataset.clone !== 'true'
			);

		const markClone = ( card ) => {
			card.dataset.clone = 'true';
			card.setAttribute( 'aria-hidden', 'true' );
			card.querySelectorAll( '[id]' ).forEach( ( el ) =>
				el.removeAttribute( 'id' )
			);
			return card;
		};

		const removeClones = () => {
			grid.querySelectorAll( '[data-clone="true"]' ).forEach( ( clone ) =>
				clone.remove()
			);
		};

		const renderDots = () => {
			if ( ! dotsContainer ) {
				return;
			}

			dotsContainer.innerHTML = '';
			const fragment = document.createDocumentFragment();

			for ( let i = 0; i < totalSlides; i++ ) {
				const dot = document.createElement( 'button' );
				dot.type = 'button';
				dot.className = 'slider-dot';
				dot.setAttribute(
					'aria-label',
					`Go to testimonial ${ i + 1 }`
				);
				dot.dataset.index = String( i );
				if ( i === currentIndex ) {
					dot.classList.add( 'active' );
				}
				fragment.appendChild( dot );
			}

			dotsContainer.appendChild( fragment );
		};

		const updateDots = () => {
			if ( ! dotsContainer ) {
				return;
			}

			Array.from(
				dotsContainer.querySelectorAll( '.slider-dot' )
			).forEach( ( dot, index ) => {
				dot.classList.toggle( 'active', index === currentIndex );
			} );
		};

		const updateControlsState = () => {
			const hasMultipleSlides = totalSlides > 1;

			if ( prevBtn ) {
				prevBtn.disabled = ! hasMultipleSlides;
			}

			if ( nextBtn ) {
				nextBtn.disabled = ! hasMultipleSlides;
			}
		};

		const measureStep = () => {
			const activeSlide =
				grid.children[ trackIndex ] ||
				grid.querySelector( '.testimonial-card' );
			if ( ! activeSlide ) {
				slideStep = 0;
				return;
			}

			const styles = window.getComputedStyle( grid );
			const gapValue = styles.columnGap || styles.gap || '0';
			const parsedGap = Number.parseFloat( gapValue );
			const gap = Number.isFinite( parsedGap ) ? parsedGap : 0;
			slideStep = activeSlide.getBoundingClientRect().width + gap;
		};

		const setTrackPosition = ( animate = true ) => {
			if ( ! isMobile() || slideStep === 0 ) {
				grid.style.transition = '';
				grid.style.transform = '';
				isAnimating = false;
				return;
			}

			const shouldAnimate = animate && ! prefersReducedMotion;
			grid.style.transition = shouldAnimate
				? 'transform 420ms cubic-bezier(0.22, 0.61, 0.36, 1)'
				: 'none';
			grid.style.transform = toTranslate( -trackIndex * slideStep );
			isAnimating = shouldAnimate;

			if ( ! shouldAnimate ) {
				syncLoopPosition();
			}
		};

		const syncLoopPosition = () => {
			if ( trackIndex === 0 ) {
				trackIndex = totalSlides;
				setTrackPosition( false );
			} else if ( trackIndex === totalSlides + 1 ) {
				trackIndex = 1;
				setTrackPosition( false );
			}
		};

		const goToSlide = ( index ) => {
			if ( totalSlides <= 1 ) {
				return;
			}

			currentIndex = ( index + totalSlides ) % totalSlides;
			trackIndex = currentIndex + 1;
			setTrackPosition( true );
			updateDots();
		};

		const nextSlide = () => {
			if ( totalSlides <= 1 || isAnimating ) {
				return;
			}

			currentIndex = ( currentIndex + 1 ) % totalSlides;
			trackIndex++;
			setTrackPosition( true );
			updateDots();
		};

		const prevSlide = () => {
			if ( totalSlides <= 1 || isAnimating ) {
				return;
			}

			currentIndex = ( currentIndex - 1 + totalSlides ) % totalSlides;
			trackIndex--;
			setTrackPosition( true );
			updateDots();
		};

		const setupTrack = () => {
			removeClones();

			const originals = getOriginalSlides();
			totalSlides = originals.length;
			currentIndex = Math.min(
				currentIndex,
				Math.max( totalSlides - 1, 0 )
			);
			trackIndex = currentIndex + 1;

			if ( totalSlides > 1 && isMobile() ) {
				const firstClone = markClone(
					originals[ 0 ].cloneNode( true )
				);
				const lastClone = markClone(
					originals[ totalSlides - 1 ].cloneNode( true )
				);
				grid.insertBefore( lastClone, originals[ 0 ] );
				grid.appendChild( firstClone );
			}

			renderDots();
			updateControlsState();
			measureStep();
			setTrackPosition( false );
		};

		const startDrag = ( clientX, clientY ) => {
			if ( ! isMobile() || totalSlides <= 1 || isAnimating ) {
				return false;
			}

			startX = clientX;
			currentX = clientX;
			startY = clientY;
			isDragging = true;
			dragActive = false;
			grid.style.transition = 'none';
			return true;
		};

		const moveDrag = ( clientX, clientY, event ) => {
			if ( ! isDragging || ! isMobile() || slideStep === 0 ) {
				return;
			}

			const deltaX = clientX - startX;
			const deltaY = clientY - startY;

			if ( ! dragActive ) {
				if ( Math.abs( deltaY ) > Math.abs( deltaX ) ) {
					isDragging = false;
					return;
				}
				dragActive = true;
			}

			event.preventDefault();
			currentX = clientX;
			grid.style.transform = toTranslate(
				-trackIndex * slideStep + deltaX
			);
		};

		const finishDrag = () => {
			if ( ! isDragging ) {
				return;
			}

			isDragging = false;
			if ( ! dragActive ) {
				setTrackPosition( true );
				return;
			}

			const deltaX = currentX - startX;
			const threshold = Math.max( 40, Math.min( 90, slideStep * 0.18 ) );

			if ( deltaX <= -threshold ) {
				nextSlide();
			} else if ( deltaX >= threshold ) {
				prevSlide();
			} else {
				setTrackPosition( true );
			}
		};

		const onTouchStart = ( event ) => {
			if (
				( activeDragSource && activeDragSource !== 'touch' ) ||
				event.touches.length === 0
			) {
				return;
			}

			const touch = event.touches[ 0 ];
			if ( startDrag( touch.clientX, touch.clientY ) ) {
				activeDragSource = 'touch';
			}
		};

		const onTouchMove = ( event ) => {
			if ( activeDragSource !== 'touch' || event.touches.length === 0 ) {
				return;
			}

			const touch = event.touches[ 0 ];
			moveDrag( touch.clientX, touch.clientY, event );
		};

		const onTouchEnd = () => {
			if ( activeDragSource !== 'touch' ) {
				return;
			}

			activeDragSource = '';
			finishDrag();
		};

		const onPointerDown = ( event ) => {
			if (
				event.pointerType === 'touch' ||
				( activeDragSource && activeDragSource !== 'pointer' )
			) {
				return;
			}

			if ( startDrag( event.clientX, event.clientY ) ) {
				activeDragSource = 'pointer';
				activePointerId = event.pointerId;
				if ( viewport.setPointerCapture ) {
					viewport.setPointerCapture( event.pointerId );
				}
				event.preventDefault();
			}
		};

		const onMouseDown = ( event ) => {
			if (
				supportsPointerEvents ||
				event.button !== 0 ||
				( activeDragSource && activeDragSource !== 'mouse' )
			) {
				return;
			}

			if ( startDrag( event.clientX, event.clientY ) ) {
				activeDragSource = 'mouse';
				event.preventDefault();
			}
		};

		const onMouseMove = ( event ) => {
			if ( activeDragSource !== 'mouse' ) {
				return;
			}

			moveDrag( event.clientX, event.clientY, event );
		};

		const onMouseUp = () => {
			if ( activeDragSource !== 'mouse' ) {
				return;
			}

			activeDragSource = '';
			finishDrag();
		};

		const onPointerMove = ( event ) => {
			if (
				activeDragSource !== 'pointer' ||
				activePointerId !== event.pointerId
			) {
				return;
			}

			moveDrag( event.clientX, event.clientY, event );
		};

		const onPointerEnd = ( event ) => {
			if (
				activeDragSource !== 'pointer' ||
				activePointerId !== event.pointerId
			) {
				return;
			}

			if (
				viewport.releasePointerCapture &&
				viewport.hasPointerCapture &&
				viewport.hasPointerCapture( event.pointerId )
			) {
				viewport.releasePointerCapture( event.pointerId );
			}

			activeDragSource = '';
			activePointerId = null;
			finishDrag();
		};

		if ( prevBtn ) {
			prevBtn.addEventListener( 'click', prevSlide );
		}

		if ( nextBtn ) {
			nextBtn.addEventListener( 'click', nextSlide );
		}

		if ( dotsContainer ) {
			dotsContainer.addEventListener( 'click', ( event ) => {
				const button = event.target.closest( '.slider-dot' );
				if ( ! button ) {
					return;
				}

				const index = Number.parseInt(
					button.dataset.index || '0',
					10
				);
				goToSlide( index );
			} );
		}

		if ( ! block.hasAttribute( 'tabindex' ) ) {
			block.tabIndex = 0;
		}

		block.addEventListener( 'keydown', ( event ) => {
			if ( ! isMobile() ) {
				return;
			}

			if ( event.key === 'ArrowLeft' ) {
				event.preventDefault();
				prevSlide();
			}

			if ( event.key === 'ArrowRight' ) {
				event.preventDefault();
				nextSlide();
			}
		} );

		grid.addEventListener( 'transitionend', ( event ) => {
			if ( event.propertyName !== 'transform' ) {
				return;
			}

			isAnimating = false;
			syncLoopPosition();
		} );

		viewport.addEventListener( 'touchstart', onTouchStart, {
			passive: true,
		} );
		viewport.addEventListener( 'touchmove', onTouchMove, {
			passive: false,
		} );
		viewport.addEventListener( 'touchend', onTouchEnd );
		viewport.addEventListener( 'touchcancel', onTouchEnd );

		if ( supportsPointerEvents ) {
			viewport.addEventListener( 'pointerdown', onPointerDown );
			viewport.addEventListener( 'pointermove', onPointerMove );
			viewport.addEventListener( 'pointerup', onPointerEnd );
			viewport.addEventListener( 'pointercancel', onPointerEnd );
		} else {
			viewport.addEventListener( 'mousedown', onMouseDown );
			window.addEventListener( 'mousemove', onMouseMove );
			window.addEventListener( 'mouseup', onMouseUp );
		}

		window.addEventListener( 'resize', () => {
			if ( resizeTimer ) {
				window.clearTimeout( resizeTimer );
			}
			resizeTimer = window.setTimeout( setupTrack, 140 );
		} );

		setupTrack();
	} );
} );
